import Vue from 'vue';
import toNumber from 'lodash/toNumber';
import dayjs, { UnitType } from 'dayjs';

import { isStoredCardExpired } from '@/core/payment-card/utilities/card';
import { StoredCard } from '@/views/checkout/components/payment-methods/payment-card/interfaces';

export const storedCardsKey = 'stored-cards';
export const storedCardsUpdateKey = 'stored-cards-updated';

export function getStoredCards(): StoredCard[] {
    const cards = JSON.parse(Vue.$localStorage.getItem(storedCardsKey) || '[]');

    return sortStoredCards(cards);
}

export function getStoredCard(id: string) {
    const cards: StoredCard[] = JSON.parse(Vue.$localStorage.getItem(storedCardsKey) || '[]');

    return cards.find((item: StoredCard): boolean => item.id === id);
}

export function allowStoredCardUpdate(card: StoredCard, storedCard?: StoredCard) {
    if (!storedCard) {
        return false;
    }

    if (!storedCard.expirationMonth || !card.expirationMonth || !storedCard.expirationYear || !card.expirationYear) {
        return false;
    }

    return storedCard.expirationMonth !== card.expirationMonth || storedCard.expirationYear !== card.expirationYear;
}

export function setStoredCards(storedCards: StoredCard[]) {
    Vue.$localStorage.setItem(storedCardsKey, JSON.stringify(storedCards));
}

export function updateStoredCardsDate() {
    Vue.$localStorage.setItem(storedCardsUpdateKey, dayjs().toJSON());
}

export function sanitizeStoredCardDate(date: string) {
    return date?.replace(/["']/g, '');
}

export function getStoredCardDate() {
    return sanitizeStoredCardDate(Vue.$localStorage.getItem(storedCardsUpdateKey) as string);
}

export function isStoredCardsDateValid(unit: UnitType = 'month', duration: number = 2) {
    const lastUpdated = getStoredCardDate();

    return lastUpdated && dayjs(dayjs()).diff(
        dayjs(lastUpdated),
        unit
    ) < duration;
}

export function updateStoredCard(card: StoredCard) {
    const storedCards = getStoredCards();
    const currentCardIdx = storedCards.findIndex((storedCard) => storedCard.id === card.id);

    if (currentCardIdx !== -1) {
        storedCards[currentCardIdx] = {
            ...storedCards[currentCardIdx],
            ...card
        };

        setStoredCards(storedCards);
        updateStoredCardsDate();
    }

    return storedCards;
}

export function updateStoredCardUsage(cardId: string) {
    const storedCards = getStoredCards();
    const card = storedCards.find((storedCard) => storedCard.id === cardId);

    if (card) {
        updateStoredCard({
            id: cardId,
            usage: card.usage + 1
        } as StoredCard);
    }
}

export function removeStoredCard(cardId: string) {
    const storedCards = getStoredCards().filter(
        (storedCard) => storedCard.id !== cardId
    );

    setStoredCards(storedCards);
    updateStoredCardsDate();

    return storedCards;
}

export function addStoredCard(card: StoredCard) {
    let storedCards = getStoredCards();
    // If a stored card with the same masked number exists and is expired,
    // we delete the old card and replace it with the new one
    const storedCard = storedCards.find((item: StoredCard): boolean => item.maskedNumber === card.maskedNumber);

    if (storedCard && isStoredCardExpired(storedCard)) {
        storedCards = removeStoredCard(storedCard.id);
    }

    storedCards.push({
        ...card,
        usage: 1,
        created: dayjs().toJSON()
    });

    setStoredCards(storedCards);
    updateStoredCardsDate();

    return storedCards;
}

function sortStoredCards(cards: StoredCard[]) {
    return cards
        .sort((a, b) => (dayjs(a.created).isAfter(dayjs(b.created)) ? -1 : 1))
        .sort((a, b) => b.usage - a.usage)
        .sort((a, b) => toNumber(isStoredCardExpired(a)) - toNumber(isStoredCardExpired(b)));
}

export function validateCardExpiration(expirationYear: string, expirationMonth: string) {
    return dayjs().isBefore(dayjs(`${expirationYear}-${expirationMonth}`).endOf('month'));
}
